/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { transaction } from 'mobx';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addDirtyState } from 'src/core/utils/decorators/addDirtyState';
import { getSimpleEntityService } from 'src/luis/core/services/entities/simpleEntityService';
import { getLuisFeatureService } from 'src/luis/core/services/luisFeatureService';
import { createConversationFeatureStore } from 'src/luis/core/stores/conversationFeatureStore';
import { featureStoreToFeatureSwagger } from 'src/luis/modules/features/handlers/mappers';
import { Feature } from 'src/luis/modules/features/types/Feature';
import { FeatureSwagger } from 'src/luis/modules/features/types/FeatureSwagger';
import {
    getModelIdFromModelName,
    getModelNameFromModelId,
    getModelStore
} from 'src/luis/modules/features/utils/conversationFeatureHandlerHelpers';
import { featureSwaggerToConversationFeatureStore, isPhraseList } from 'src/luis/modules/features/utils/conversationFeatureUtils';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { instanceOfDeNormalizer } from 'src/luis/projects/entities/utils/entitiesDenormalizer';
import { getEntityHandlerGroupUtils } from 'src/luis/projects/entities/utils/entityHelpers';
import { Model } from 'src/luis/types/Model';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { FeatureStore } from 'src/luis/types/stores/features/FeatureStore';

export type ConversationFeatureHandlerGroup = ReturnType<typeof createConversationFeatureHandlerGroup>;

/**
 * Creates a features handler group for model
 * related operations.
 *
 * @link Check out dispatcher.md
 *
 * @param dependencies The dependencies needed for the handler group.
 */
export const createConversationFeatureHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const { getEntityStore, getAppVersionStore } = getEntityHandlerGroupUtils(rootStore);
    const logger = getLogger();

    /**
     * Loads the features for the given model, application and version.
     *
     * @param param0.model The model id and its type to get the features for.
     */
    const loadConversationModelFeatures = async ({ model, context }: HandlerParams<{ model: Omit<Model, 'name'> }>) => {
        const { appId, versionId } = context;
        const featureService = getLuisFeatureService({ context });
        const modelStore = getModelStore({ appId, versionId, model, rootStore });

        if (!context.forceRefresh && (modelStore.featureStoreHouse.isLoading || modelStore.featureStoreHouse.isLoaded)) {
            return;
        }
        const logTimedActivity = logger.createTimedActivityLogger<FeatureSwagger[]>('LoadConversationModelFeatures', () => ({
            modelId: modelStore.id
        }));

        const featureSwaggers: FeatureSwagger[] = await logTimedActivity(featureService.getFeatures({ model }));

        const features: FeatureStore[] = featureSwaggers.map(swagger =>
            createConversationFeatureStore(
                getModelIdFromModelName({
                    appId,
                    versionId,
                    featureName: isPhraseList(swagger) ? swagger.featureName : swagger.modelName,
                    rootStore
                }),
                {
                    typeId: isPhraseList(swagger) ? 'feature' : 'model',
                    name: isPhraseList(swagger) ? swagger.featureName : swagger.modelName,
                    isRequired: swagger.isRequired
                }
            )
        );

        modelStore.featureStoreHouse.setItems(features);
    };

    /**
     * Adds feature for the given model, application and version.
     *
     * @param param0.model The model id and its type to add the features to.
     */
    const addConversationFeature = async ({
        model,
        feature,
        rootEntityId,
        context
    }: HandlerParams<{ model: Omit<Model, 'name'>; feature: Feature; rootEntityId: LuisId }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const featureService = getLuisFeatureService({ context });

            const modelStore = getModelStore({ appId, versionId, model, rootStore });
            const logTimedActivity = logger.createTimedActivityLogger('AddConversationFeature', () => ({ modelId: modelStore.id }));

            await logTimedActivity(
                featureService.addFeature({
                    model,
                    feature: featureStoreToFeatureSwagger({
                        name: getModelNameFromModelId({ appId, versionId, featureId: feature.id, rootStore }),
                        typeId: feature.typeId === 'feature' ? 'feature' : 'model',
                        isRequired: feature.isRequired
                    })
                })
            );

            modelStore.featureStoreHouse.add(
                createConversationFeatureStore(feature.id, {
                    typeId: feature.typeId === 'feature' ? 'feature' : 'model',
                    name: feature.name,
                    isRequired: false
                }),
                'back'
            );

            if (rootEntityId) {
                (<SimpleEntityStore>getEntityStore({ appId, versionId, entityId: rootEntityId })).incrementUpdateCount();
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    /**
     * deletes features for the given model, application and version.
     *
     * @param param0.model The model id and its type to delete the features from.
     */
    const deleteConversationFeature = async ({
        model,
        feature,
        rootEntityId,
        tracker,
        context
    }: HandlerParams<{ model: Omit<Model, 'name'>; feature: Feature; rootEntityId?: LuisId }>) =>
        flow(async () => {
            try {
                const { appId, versionId } = context;
                const modelStore = getModelStore({ appId, versionId, model, rootStore });
                const rootEntityStore = <SimpleEntityStore>getEntityStore({ appId, versionId, entityId: rootEntityId });

                // If deleting a feature from a simple entity AND
                // If deleted feature is coming from instanceOf, set instance of to null and delete it from the store.
                const isOldInstanceOf =
                    feature.isRequired &&
                    modelStore.typeId === ModelType.Simple &&
                    (<SimpleEntityStore>modelStore).instanceOf &&
                    (<SimpleEntityStore>modelStore).instanceOf === feature.id;
                if (isOldInstanceOf) {
                    const simpleEntityService = getSimpleEntityService({ context });
                    const versionStore = getAppVersionStore({ appId, versionId });

                    await simpleEntityService.updateChildEntity({ id: model.id, instanceOf: null });

                    const childEntityStore = rootEntityStore.getChildById(model.id);
                    childEntityStore.deleteInstanceOf();
                    instanceOfDeNormalizer(childEntityStore, versionStore.entityStoreHouse.items);

                    rootEntityStore.incrementUpdateCount();

                    return;
                }

                // If deleted feature is a normal feature, delete it using feature service.
                const featureService = getLuisFeatureService({ context });
                const logTimedActivity = logger.createTimedActivityLogger('DeleteConversationFeature', () => ({ modelId: modelStore.id }));

                await logTimedActivity(
                    featureService.deleteFeature({
                        model,
                        feature: featureStoreToFeatureSwagger({
                            name: getModelNameFromModelId({ appId, versionId, featureId: feature.id, rootStore }),
                            typeId: feature.typeId === 'feature' ? 'feature' : 'model',
                            isRequired: feature.isRequired
                        })
                    })
                );

                modelStore.featureStoreHouse.removeById(feature.id);

                if (feature.typeId === 'feature') {
                    rootStore.currentLuisAppStore.currentAppVersionStore.phraseListStoreHouse.findById(feature.id).setIsGlobal(false);
                }

                if (rootEntityStore) {
                    rootEntityStore.incrementUpdateCount();
                }
            } finally {
                if (tracker) {
                    tracker.setContext(feature.id);
                }
            }
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    const toggleConversationFeatureRequired = async ({
        model,
        rootEntityId,
        features,
        featureId,
        isRequired,
        tracker,
        context
    }: HandlerParams<{ model: SimpleEntity; rootEntityId: LuisId; features: Feature[]; featureId: LuisId; isRequired: boolean }>) =>
        flow(async () => {
            const { appId, versionId } = context;
            const modelStore = <SimpleEntityStore>getModelStore({ appId, versionId, model, rootStore });
            const rootEntityStore = <SimpleEntityStore>getEntityStore({ appId, versionId, entityId: rootEntityId });
            const featureService = getLuisFeatureService({ context });

            // If toggled feature On and it has a instanceOf from before
            const toggledNonInstanceOfOn = isRequired && modelStore.instanceOf && modelStore.instanceOf !== featureId;
            // If toggled feature Off and it's a instanceOf from before
            const toggledInstanceOfOff = !isRequired && modelStore.instanceOf && modelStore.instanceOf === featureId;

            try {
                if (toggledNonInstanceOfOn || toggledInstanceOfOff) {
                    const instanceOfFeature = features.find(f => f.id === modelStore.instanceOf);
                    instanceOfFeature.isRequired = false;
                    // Set instance of to null
                    const simpleEntityService = getSimpleEntityService({ context });
                    const versionStore = getAppVersionStore({ appId, versionId });

                    // Delete instanceOf
                    await simpleEntityService.updateChildEntity({ id: model.id, instanceOf: null });

                    // Add instanceOf as a feature that is not required
                    await featureService.addFeature({
                        model,
                        feature: featureStoreToFeatureSwagger({ name: instanceOfFeature.name, typeId: 'model', isRequired: false })
                    });

                    transaction(() => {
                        const childEntityStore = rootEntityStore.getChildById(model.id);
                        childEntityStore.deleteInstanceOf();
                        instanceOfDeNormalizer(childEntityStore, versionStore.entityStoreHouse.items);
                        modelStore.featureStoreHouse.add(
                            createConversationFeatureStore(instanceOfFeature.id, {
                                typeId: instanceOfFeature.typeId === 'feature' ? 'feature' : 'model',
                                name: instanceOfFeature.name,
                                isRequired: false
                            }),
                            'back'
                        );
                    });
                }

                // If any feature other than the instanceOf is toggled, call feature API to update
                if (!toggledInstanceOfOff) {
                    // Set the new feature as required.
                    const featureStores = features.map(featureSwaggerToConversationFeatureStore);
                    const featureSwaggers = features.map(f =>
                        featureStoreToFeatureSwagger({
                            name: getModelNameFromModelId({ appId, versionId, featureId: f.id, rootStore }),
                            typeId: f.typeId === 'feature' ? 'feature' : 'model',
                            isRequired: f.isRequired
                        })
                    );

                    await featureService.updateFeatures({ model, features: featureSwaggers });

                    modelStore.featureStoreHouse.setItems(featureStores);
                }
            } finally {
                if (tracker) {
                    tracker.setContext(featureId);
                }
            }

            rootEntityStore.incrementUpdateCount();
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    return {
        loadConversationModelFeatures,
        addConversationFeature,
        deleteConversationFeature,
        toggleConversationFeatureRequired
    };
};
